/**
 * Qentoros business contact details — the single source for anything a customer
 * might use to reach the company.
 *
 * These strings were duplicated across the app: the phone number appeared in five
 * places in three different `tel:` formats (the Contact page's `tel:5402270203`
 * was missing the +1 country code the footer and Support page both included), the
 * office address in five, and the support hours in exactly one — the Support page
 * — which is why they had quietly drifted. A number that's wrong in one place is
 * worse than a number that's missing: a clinic dials it and reaches nobody.
 *
 * Anything that appears on more than one screen belongs here. Where a value has
 * both a human form and a machine form (a displayed number vs. a `tel:` href),
 * both live here together so they cannot disagree.
 *
 * Sibling of src/config/admins.js — same purpose, different subject.
 */

// --- Phone ------------------------------------------------------------------

// E.164 for the href; the dashed form is what a human reads. Kept adjacent so
// editing one without the other is obviously wrong.
export const PHONE_E164 = "+15402270203";
export const PHONE_DISPLAY = "540-227-0203";
export const PHONE_HREF = `tel:${PHONE_E164}`;

// --- Email ------------------------------------------------------------------

/**
 * Group inboxes. These mirror the sending addresses in
 * supabase/functions/_shared/senders.ts — mail sent FROM `orders@` should be
 * replied to at `orders@`, so the two lists must stay in step.
 */
export const EMAIL = {
  support: "support@qentoros.com",
  orders: "orders@qentoros.com",
  info: "info@qentoros.com",
  customers: "customers@qentoros.com",
};

/** `mailto:` href for a group inbox. */
export const mailto = (group = "support") =>
  `mailto:${EMAIL[group] || EMAIL.support}`;

// --- Hours ------------------------------------------------------------------

/**
 * Staffed hours.
 *
 * Structured as well as displayed, so a future "we're currently closed" badge or
 * a schema.org openingHours block doesn't have to parse the prose back apart.
 * `timezone` is the IANA name for anything that needs to compute; `timezoneLabel`
 * is what a person reads — "EST" is what customers recognise even though the
 * office observes EDT for half the year, which is why the label is a separate
 * string from the zone rather than derived from it.
 */
export const HOURS = {
  days: "Monday – Friday",
  daysShort: "Mon–Fri",
  open: "9:00 AM",
  close: "5:00 PM",
  timezone: "America/New_York",
  timezoneLabel: "EST",
};

/** "9:00 AM – 5:00 PM EST" */
export const HOURS_RANGE = `${HOURS.open} – ${HOURS.close} ${HOURS.timezoneLabel}`;

/** "Monday – Friday, 9:00 AM – 5:00 PM EST" — the full statement. */
export const HOURS_FULL = `${HOURS.days}, ${HOURS_RANGE}`;

// --- Response times ---------------------------------------------------------

// What the support page promises. Stated once so the email copy, the form's
// confirmation, and the contact card can't promise three different things.
export const RESPONSE_TIME = {
  email: "within 24 hours",
  typical: "24–48 hours",
};

// --- Address ----------------------------------------------------------------

export const ADDRESS = {
  street: "2903 Commerce Street",
  suite: "Suite C",
  city: "Blacksburg",
  state: "VA",
  zip: "24060",
};

/** "2903 Commerce Street, Suite C" */
export const ADDRESS_LINE1 = `${ADDRESS.street}, ${ADDRESS.suite}`;
/** "Blacksburg, VA 24060" */
export const ADDRESS_LINE2 = `${ADDRESS.city}, ${ADDRESS.state} ${ADDRESS.zip}`;
/** "Blacksburg, VA" — the short locality the footer and clinic list use. */
export const LOCALITY = `${ADDRESS.city}, ${ADDRESS.state}`;
/** Single-line form, for meta tags and print headers. */
export const ADDRESS_ONE_LINE = `${ADDRESS_LINE1}, ${ADDRESS_LINE2}`;

/**
 * Street + locality with a non-breaking space before the state, so the Contact
 * page's "Find us" heading can't wrap between "Blacksburg" and "VA".
 */
export const ADDRESS_HEADLINE = `${ADDRESS.street}, ${ADDRESS.city}\u00a0${ADDRESS.state}`;
